import { TableV3ColDefs } from 'common/table';
import { ICellRendererParams } from 'ag-grid-community';
import { CandidatesTableColumns } from '../types/CandidatesTable';
import { Typography, Link, Box } from '@mui/material';
import { formatDateFromNow } from 'utils/datetime';
import { candidateStatusOrder } from 'modules/analyse/constants/candidateStatusOrder';
import { roundToTwoDecimals } from 'utils/fomatting';
import {
  CandidateStatus,
  SearchableCandidateSortableFields,
  CandidateOrigin
} from 'generated-graphql/types';
import { StarIcon } from 'common/icons/StarIcon';
import { CandidateStatusChip } from 'modules/analyse/components/CandidateStatusChip';
import { AnchorText } from 'components/Text';
import { candidateStatusLabelMap } from 'services/candidate/utils/candidateStatusLabelMap';
import { Lock, LockOpen, Edit as EditIcon } from '@mui/icons-material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { ActionOption, ActionPopover } from 'components/ActionPopover';
import { EyeIcon } from 'components/Icons/EyeIcon';
import { ConversationStatusIconAndLabel } from 'modules/conversations/components';
import { isNotNull } from 'core/utils/isNotNull';
import { toPercentage } from 'utils/calculations';
import { IntegrationCredentials } from 'services/integration/types';
import { formatAtsProviderName } from 'core/utils/formatAtsProviderName';
import { IconTooltip } from 'common/ui';
import { ExpandableText } from 'common/ui/ExpandableText';
import { AnalysisErrorChip } from 'services/candidate/components/AnalysisError';

type CandidatesTableColumnsProps = {
  hasCampaign: boolean;
  onOpenCandidateDrawer: (
    candidateId: string,
    index: number,
    defaultCampaignTab: boolean
  ) => void;
  openChangeStatusDrawer: (candidateId: string) => void;
  onLockStatus: (candidateId: string) => void;
  openEditModal: (candidateId: string) => void;
  isReadOnly: boolean;
  isAtsApplicationSource: boolean;
  integration: IntegrationCredentials | null;
};

export const candidatesTableColumns = ({
  hasCampaign,
  onOpenCandidateDrawer,
  openChangeStatusDrawer,
  onLockStatus,
  openEditModal,
  isReadOnly,
  isAtsApplicationSource,
  integration
}: CandidatesTableColumnsProps): TableV3ColDefs<
  CandidatesTableColumns,
  SearchableCandidateSortableFields
> => [
  {
    field: 'isLocked',
    headerName: '',
    headerIcon: <Lock fontSize="small" sx={{ color: 'black' }} />,
    minWidth: 80,
    width: 80,
    cellStyle: {
      display: 'flex',
      alignItems: 'flex-start'
    },
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      if (!params.data) return null;
      return params.data.isLocked ? (
        <Lock color="primary" fontSize="small" />
      ) : null;
    },
    filterOptions: {
      items: [
        {
          value: 'true',
          label: 'Locked'
        },
        {
          value: 'false',
          label: 'Unlocked'
        }
      ],
      selectionType: 'single'
    }
  },
  {
    field: 'applicantName',
    headerName: 'Applicant',
    flex: 1,
    sortField: SearchableCandidateSortableFields.FirstName,
    searchable: true,
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      const data = params.data;
      if (!data) return null;
      return (
        <Box display="flex" width="100%" gap="10px" alignItems="center">
          <Link
            component="button"
            onClick={() =>
              onOpenCandidateDrawer(data.id, params.node?.rowIndex ?? 0, false)
            }
            sx={{
              textTransform: 'capitalize'
            }}
          >
            <AnchorText>
              {data.firstName.toLowerCase()} {data.lastName}
            </AnchorText>
          </Link>
          {data.isAllRequirementsMet && (
            <IconTooltip title="Met knockout requirements">
              <span style={{ display: 'inline-flex' }}>
                <StarIcon width={20} height={20} />
              </span>
            </IconTooltip>
          )}
        </Box>
      );
    }
  },
  {
    field: 'totalScoreScaled',
    headerName: 'Score',
    width: 100,
    sortField: SearchableCandidateSortableFields.TotalScoreScaled,
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      const data = params.data;
      if (!data) return null;
      const { totalScoreScaled } = data;

      if (data.analysisErrorCode) {
        return <AnalysisErrorChip code={data.analysisErrorCode} />;
      }

      if (!isNotNull(totalScoreScaled)) {
        return <Typography>-</Typography>;
      }

      return (
        <Link
          component="button"
          onClick={() => {
            onOpenCandidateDrawer(data.id, 0, false);
          }}
          sx={{
            display: 'flex',
            gap: '4px',
            alignItems: 'center'
          }}
        >
          {roundToTwoDecimals(totalScoreScaled * 100)}%
        </Link>
      );
    }
  },
  {
    field: 'conversationPreferredAnswersScore',
    headerName: 'Campaign',
    width: 100,
    hide: !hasCampaign,
    cellStyle: {
      display: 'flex',
      justifyItems: 'center'
    },
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      const data = params.data;
      if (!data) return null;
      const { conversationId, conversationStatus, conversationNeedsReview } =
        data;

      if (!conversationId || !conversationStatus) {
        return '-';
      }

      const preferredAnswersScore = isNotNull(
        data.conversationPreferredAnswersScore
      )
        ? toPercentage(data.conversationPreferredAnswersScore as number)
        : undefined;

      return (
        <Box display="flex" gap="4px" alignItems="center">
          <Link
            component="button"
            onClick={() => {
              onOpenCandidateDrawer(data.id, params.node?.rowIndex ?? 0, true);
            }}
            sx={{
              display: 'flex',
              gap: '4px',
              alignItems: 'center'
            }}
          >
            {preferredAnswersScore}
          </Link>
          <ConversationStatusIconAndLabel
            status={conversationStatus}
            needsReview={conversationNeedsReview ?? false}
          />
        </Box>
      );
    }
  },
  {
    field: 'status',
    headerName: 'Popp status',
    width: 180,
    cellStyle: {
      display: 'flex',
      alignItems: 'flex-start'
    },
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      if (!params.data) return null;
      return <CandidateStatusChip status={params.value as CandidateStatus} />;
    },
    filterOptions: {
      items: (Object.values(CandidateStatus) as Array<CandidateStatus>)
        .map((status) => ({
          value: status,
          label: candidateStatusLabelMap[status]
        }))
        .sort((a, b) => {
          return (
            (candidateStatusOrder[a.value] || -1) -
            (candidateStatusOrder[b.value] || -1)
          );
        }),
      selectionType: 'multiple'
    }
  },
  {
    field: 'origin',
    headerName: 'Origin',
    flex: 1,
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      const getOriginLabel = (origin: string | null): string => {
        if (!origin) return '-';

        switch (origin) {
          case CandidateOrigin.ExternallySourcedViaContactOut:
            return 'Popp external';
          case CandidateOrigin.InternallySourcedViaAts:
            return 'Popp internal';
          case CandidateOrigin.NonAtsCvUpload:
            return 'Uploaded manually';
          case CandidateOrigin.NonAtsTypeform:
            return 'Application link';
          default:
            return '-';
        }
      };

      return (
        <Typography variant="body2">{getOriginLabel(params.value)}</Typography>
      );
    },
    filterOptions: {
      items: [
        {
          value: CandidateOrigin.ExternallySourcedViaContactOut,
          label: 'Popp external'
        },
        {
          value: CandidateOrigin.InternallySourcedViaAts,
          label: 'Popp internal'
        },
        {
          value: CandidateOrigin.NonAtsCvUpload,
          label: 'Uploaded manually'
        },
        {
          value: CandidateOrigin.NonAtsTypeform,
          label: 'Application link'
        }
      ],
      selectionType: 'multiple'
    }
  },
  {
    field: 'atsApplicationSource',
    hide: !isAtsApplicationSource,
    headerName: `${formatAtsProviderName(integration?.provider)} source`,
    flex: 1,
    minWidth: 210,
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      if (!params.data) return null;
      const value = params.value as string | null | undefined;
      if (!value) {
        return <Typography variant="body2">-</Typography>;
      }
      return <ExpandableText text={value} characterLimit={40} />;
    },
    searchable: true
  },
  {
    field: 'updatedAt',
    headerName: 'Last activity',
    width: 140,
    sortField: SearchableCandidateSortableFields.UpdatedAt,
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      if (!params.data) return null;
      return formatDateFromNow(params.data.updatedAt);
    }
  },
  {
    field: 'atsCandidateId',
    headerName: 'ID',
    flex: 1,
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      return params.value ?? '-';
    }
  },
  {
    field: 'id',
    headerName: '',
    width: 45,
    cellStyle: {
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'flex-start'
    },
    cellRenderer: (params: ICellRendererParams<CandidatesTableColumns>) => {
      const data = params.data;
      if (!data || isReadOnly) {
        return null;
      }

      const options: ActionOption[] = [
        {
          label: 'View',
          onClick: () => onOpenCandidateDrawer(data.id, 0, false),
          icon: <EyeIcon />,
          itemColor: 'primary'
        },
        {
          label: 'Edit',
          onClick: () => openEditModal(data.id),
          icon: <EditIcon color="primary" />,
          itemColor: 'primary'
        },
        {
          label: data.isLocked ? 'Unlock status' : 'Lock status',
          onClick: () => onLockStatus(data.id),
          icon: data.isLocked ? (
            <LockOpen color="primary" />
          ) : (
            <Lock color="primary" fontSize="small" />
          ),
          itemColor: 'primary'
        }
      ];

      if (!data.isLocked) {
        options.push({
          label: 'Change status',
          onClick: () => openChangeStatusDrawer(data.id),
          icon: <SwapHorizIcon color="primary" />,
          itemColor: 'primary'
        });
      }

      return <ActionPopover id={data.id} options={options} />;
    }
  }
];
